<template>
  <Dialog
    lg
    :title="
      deliveryNoteId
        ? `Edit Delivery Note #${deliveryNoteId} (Order#: ${order_info.order_no})`
        : `Create Delivery Note (Order#: ${order_info.order_no})`
    "
    show-close-button
    @close-event="closeModal"
    :loading="loading"
    show-footer
    showCancelButton
    showSaveButton
    :save-button-text="deliveryNoteId ? 'Update' : 'Save'"
    @save-event="saveDeliveryNotes"
    @cancel-event="closeModal"
  >
    <template #modal-content>
      <div class="p-6 space-y-6">
        <!-- Ship To -->
        <div class="border rounded-lg p-4 bg-surface-2">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="font-medium">Ship To (Original)</h3>
              <p class="text-sm">{{ address.name }}</p>
              <p class="text-sm">{{ address.address1 }}</p>
              <p v-if="address.address2" class="text-sm">{{ address.address2 }}</p>
              <p class="text-sm">{{ address.postalCode }} {{ address.city }}</p>
              <p class="text-sm">{{ address.state }}</p>
              <p class="text-sm">{{ address.country }}</p>

              <ol v-if="shipping_address_update_log.length > 0" class="mt-2 list-disc list-inside">
                <li v-for="(log, log_index) in shipping_address_update_log" :key="log_index">
                  {{ log }}
                </li>
              </ol>
            </div>
            <button @click="showAddressModal = true" class="btn">
              <PencilIcon class="w-5 h-5" />
              Edit address
            </button>
          </div>
        </div>
        <!-- Ship To -->
        <div class="border rounded-lg p-4 bg-surface-2" v-if="edittedAddress">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="font-medium">Edited Address</h3>
              <p class="text-sm">{{ edittedAddress.name }}</p>
              <p class="text-sm">{{ edittedAddress.address1 }}</p>
              <p v-if="edittedAddress.address2" class="text-sm">{{ edittedAddress.address2 }}</p>
              <p class="text-sm">{{ edittedAddress.postalCode }} {{ edittedAddress.city }}</p>
              <p class="text-sm">{{ edittedAddress.state }}</p>
              <p class="text-sm">{{ edittedAddress.country }}</p>
            </div>
          </div>
        </div>
        <div class="flex flex-col w-full gap-2 py-2">
          <div class="boxes-header w-full flex justify-between items-center flex-wrap">
            <h3 class="text-xl bold">Boxes</h3>
            <div class="flex items-center gap-2">
              <Select
                v-model="selectedBox"
                :options="
                  JSON.parse(
                    JSON.stringify([...new Map(boxOptions.map((box) => [box.label, box])).values()])
                  )
                "
                placeholder="Select Box"
                label="label"
                :clearable="false"
                @option:selected="addBox"
                class="min-w-[180px]"
              />
              <button class="btn btn-primary w-max" @click="addBox(null)">
                <PlusIcon class="w-5 h-5" /> Add Custom Box
              </button>
            </div>
          </div>
          <div class="boxes grid grid-cols-2 gap-4">
            <div
              v-for="(box, index) in boxes"
              :key="`${index}_${boxes.length}`"
              class="border p-4 rounded-lg shadow-sm bg-surface"
            >
              <div class="flex justify-between items-center mb-4 gap-2">
                <div class="flex flex-col gap-1 w-full">
                  <div class="flex gap-1 w-full">
                    <label class="block text-sm font-medium mb-1">Box Label</label>
                    <input
                      v-model="box.label"
                      type="text"
                      placeholder="Box Label"
                      class="input w-full"
                      :disabled="!box.is_custom_box"
                    />
                  </div>
                </div>
                <button
                  v-if="boxes.length > 1"
                  @click="removeBox(index, box.label, box.id)"
                  class="text-red-500 hover:text-red-700"
                >
                  <Trash2 class="w-5 h-5" />
                </button>
              </div>

              <div class="flex flex-col gap-1 w-full mb-4">
                <div class="flex gap-6 w-full">
                  <label class="block text-sm font-medium  mb-4">Box ID</label>
                  <input
                    v-model="box.box_id"
                    type="text"
                    placeholder="Box ID w-4"
                    class="input "
                    @input="onBoxIdChange(box)"
                  />
                </div>
              </div>

              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">Length (cm)</label>
                  <input
                    v-model="box.length"
                    type="number"
                    class="input"
                    min="1"
                    step="1"
                    @input="sanitizeInteger(box, 'length')"
                    @keydown="onKeydownInteger"
                    @paste="onPasteInteger(box, 'length', $event)"
                    :class="{ 'has-error': parseFloat(box.length) <= 0 }"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">Width (cm)</label>
                  <input
                    v-model="box.width"
                    type="number"
                    class="input"
                    min="1"
                    step="1"
                    @input="sanitizeInteger(box, 'width')"
                    @keydown="onKeydownInteger"
                    @paste="onPasteInteger(box, 'width', $event)"
                    :class="{ 'has-error': parseFloat(box.width) <= 0 }"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">Height (cm)</label>
                  <input
                    v-model="box.height"
                    type="number"
                    class="input"
                    min="1"
                    step="1"
                    @input="sanitizeInteger(box, 'height')"
                    @keydown="onKeydownInteger"
                    @paste="onPasteInteger(box, 'height', $event)"
                    :class="{ 'has-error': parseFloat(box.height) <= 0 }"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">Weight (kg)</label>
                  <input
                    v-model="box.weight"
                    type="number"
                    class="input"
                    min="0.1"
                    step="0.1"
                    @input="sanitizeWeightInteger(box, 'weight')"
                    :class="{ 'has-error': parseFloat(box.weight) <= 0 }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <!-- Add Datatable here -->
          <DeliveryNotesTable
            :items="items"
            :boxes="boxes"
            :order_info="{...order_info, po_number: orderPONumber}"
            ref="deliveyNotesTableRef"
            :shipment-address="edittedAddress ?? address"
            :key="isEditting"
          />
        </div>
      </div>
    </template>
  </Dialog>
  <Transition name="fade">
    <AddressModal
      v-if="showAddressModal"
      v-model="showAddressModal"
      :initial-address="address"
      @close="() => (showAddressModal = false)"
      @save="updateAddress"
    />
  </Transition>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { checkIsEmpty } from '@/composables';
import { showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { DeliveryShippingAddress } from '@/models';
import { PencilIcon, PlusIcon, Trash2 } from 'lucide-vue-next';
import { computed, defineExpose, reactive, Ref, ref } from 'vue';
import AddressModal from './AddressModal.vue';
import DeliveryNotesTable from './DeliveryNotesTable.vue';

interface LineItem {
  line_id: number;
  factory_product_id: string;
  product_name: string;
  size: string;
  sku_number: number;
  has_name_number: boolean;
  name: string;
  number: string;
  qty_original: number;
  already_shipped: number;
  qty_ordered: number;
  qty_to_ship: number;
  delivery_note_id: any;
  box: Box;
  qty_outstanding: number;
  not_included: boolean;
}

interface Box {
  id: number;
  label: string;
  length: string;
  width: string;
  height: string;
  weight: string;
  is_custom_box: boolean;
  box_id: number | string;
}

interface Props {
  order_info: {
    order_item_id: number;
    order_no: string;
  };
}
const props = defineProps<Props>();

const boxOptions = ref<Box[]>([]);
const deliveyNotesTableRef = ref(null);
const loading = ref<boolean>(false);
const showAddressModal = ref(false);
// const expandedRows = ref<Set<string | number>>(new Set());
const selectedBox = ref('');
const isEditting = ref(0);
const deliveryNoteId = ref(null);
const emits = defineEmits(['close', 'save']);
const boxes = ref<Box[]>([]);
const orderPONumber = ref('');
const items = ref<LineItem[]>([]);
const rawAddress = ref<Record<string, any>>(null);
const shipping_address_update_log: Ref<string[]> = ref([]);
const nextBoxIdCounter = ref<number>(1); // Tracks the next available box ID from server
const address = reactive<DeliveryShippingAddress>({
  name: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  state: '',
  country: '',
});
const edittedAddress = ref<DeliveryShippingAddress>(null);

// Extract the word "box" from label (case-insensitive)
const extractBoxKeyword = (label: string): string => {
  if (!label) return 'box';

  // Match "box" or "Box" anywhere in the string and extract it
  const match = label.match(/\bbox\b/i);
  return match ? match[0].toLowerCase() : 'box';
};

// Generate box_id using nextBoxIdCounter and box keyword from label
const generateBoxId = (label: string): string => {
  if (!label || !label.trim()) return `box-${nextBoxIdCounter.value}`;

  const boxKeyword = extractBoxKeyword(label);

  // Return format: "boxKeyword-counter" (e.g., "box-4", "testbox-5")
  return `${boxKeyword}-${nextBoxIdCounter.value}`;
};

// Handle box label change
const onBoxLabelChange = (box: Box, index: number) => {
  // Only regenerate if box_id was not manually edited
  if (!(box as any).box_id_manual) {
    box.box_id = generateBoxId(box.label);
  }
  notifyTableOfBoxChange();
};

// Handle box ID manual change
const onBoxIdChange = (box: Box) => {
  // Mark that this box_id was manually edited
  if (!box.hasOwnProperty('box_id_manual')) {
    Object.defineProperty(box, 'box_id_manual', {
      value: true,
      writable: true,
      enumerable: false, // Don't include in JSON.stringify
      configurable: true
    });
  } else {
    (box as any).box_id_manual = true;
  }
  notifyTableOfBoxChange();
};

// Notify the table component about box changes
const notifyTableOfBoxChange = () => {
  isEditting.value++;
};

// Watch for label changes and regenerate box_id only if not manually edited
watch(
  () => boxes.value.map((b, idx) => ({ label: b.label, index: idx })),
  (newData, oldData) => {
    newData.forEach((current, idx) => {
      const old = oldData?.[idx];
      const box = boxes.value[idx];

      // Only regenerate if:
      // 1. Label changed
      // 2. box_id was not manually edited
      if (current.label !== old?.label && !(box as any).box_id_manual) {
        box.box_id = generateBoxId(box.label);
        notifyTableOfBoxChange();
      }
    });
  },
  { deep: true }
);

const addBox = (box?: Box) => {
  let newBox: Box;

  if (box) {
    newBox = JSON.parse(JSON.stringify(box));
    delete (newBox as any).box_id_manual;
  } else {
    newBox = {
      id: boxes.value.length + 1,
      height: '0',
      label: 'Custom Box',
      length: '0',
      weight: '0',
      width: '0',
      is_custom_box: true,
      box_id: '' // Will be set below
    };
  }

  // Set box_id using generateBoxId (extracts box keyword from label)
  newBox.box_id = generateBoxId(newBox.label);

  // Push the box first, THEN increment the counter
  boxes.value.push(newBox);
  nextBoxIdCounter.value++; // Increment for next box

  selectedBox.value = '';
  notifyTableOfBoxChange();
};

const removeBox = (index: number, label: string, box_id: number) => {
  // Remove the box at the given index
  boxes.value.splice(index, 1);

  // Unassign this box from any items
  items.value.forEach((item) => {
    if (item.box?.id === box_id) {
      item.box = null;
    }
  });

  notifyTableOfBoxChange();
};

const updateAddress = (newAddress: DeliveryShippingAddress) => {
  edittedAddress.value = newAddress;
};
const closeModal = () => {
  emits('close');
};

// Fetch delivery boxes for dynamic boxOptions
const fetchDeliveryBoxes = async () => {
  try {
    const { data } = await http.get('/delivery-box');
    const list = data || [];
    boxOptions.value = list.map((b: any) => ({
      id: b.id,
      label: b.box_label ?? 'Box',
      box_id: b.box_id ?? '', // Add box_id from API response
      length: String(b?.dimensions?.length ?? ''),
      width: String(b?.dimensions?.width ?? ''),
      height: String(b?.dimensions?.height ?? ''),
      weight: String(b?.dimensions?.weight ?? ''),
    }));

   if (boxes.value.length === 0 && boxOptions.value?.length > 0) {
      const firstBox = JSON.parse(JSON.stringify({ ...boxOptions.value[0] }));
      if (nextBoxIdCounter.value > 0) {
        firstBox.box_id = generateBoxId(firstBox.label);
        Object.defineProperty(firstBox, 'box_id_manual', {
          value: true,
          writable: true,
          enumerable: false,
          configurable: true
        });
        boxes.value.push(firstBox);
      }
    }
  } catch (e) {
    handleResponseException(e);
  }
};

// Fetch delivery note table data
const fetchDeliveryNoteData = async (delivery_note_id: number | null = null) => {
  try {
    const { data } = await http.post(`/get-delivery-note-data`, {
      order_item_id: props.order_info.order_item_id,
      delivery_note_id,
    });
    orderPONumber.value = data?.po_number || '';

    if (delivery_note_id) {
      setBoxesFromResponse(data?.delivery_boxes, data?.next_box_id);
    } else {
      boxes.value = [];
      if (data?.next_box_id) {
        nextBoxIdCounter.value = data.next_box_id;
      }
    }
    setAddressFromResponse(data?.shipping_address);
    shipping_address_update_log.value = data?.shipping_address_update_log || [];
    const shippingLines: LineItem[] = data?.lines || [];
    items.value = shippingLines.map((line: LineItem & { box_id: number }, _idx: number) => {
      const box = boxes.value.find((box_opt) => box_opt.id === line.box_id);
      return {
        line_id: line.line_id,
        size: line.size,
        has_name_number: line.has_name_number,
        box: box,
        factory_product_id: line.factory_product_id,
        name: line.name,
        number: line.number,
        sku_number: line.sku_number,
        product_name: line.product_name,
        delivery_note_id: line.delivery_note_id,
        qty_ordered: line.qty_ordered,
        qty_to_ship: line.qty_to_ship,
        qty_outstanding: line.qty_outstanding,
        already_shipped: line.already_shipped,
        qty_original: line.qty_original,
        not_included: Boolean(line.not_included),
      };
    });
  } catch (e) {
    handleResponseException(e);
  } finally {
    loading.value = false;
  }
};

const setAddressFromResponse = (shipping: any) => {
  rawAddress.value = shipping;
  address.name = shipping.original?.name.trim();
  address.address1 = shipping.original?.address1 || '';
  address.address2 = shipping.original?.address2 || '';
  address.postalCode = shipping.original?.postalCode ?? '';
  address.city = shipping.original?.city || '';
  address.country = shipping.original?.country || '';
  if (shipping.edited) {
    edittedAddress.value = {
      name: shipping.edited?.name.trim(),
      address1: shipping.edited?.address1,
      address2: shipping.edited?.address2,
      postalCode: shipping.edited?.postalCode,
      city: shipping.edited?.city,
      state: shipping.edited?.state,
      country: shipping.edited?.country,
    };
  }
};

const setBoxesFromResponse = (deliveryBoxes: any, next_box_id?: number) => {
  boxes.value = [];
  deliveryBoxes.forEach((box: any) => {
    const newBox: Box = {
      id: box.id,
      label: box.box_label,
      length: box.dimensions.length,
      width: box.dimensions.width,
      height: box.dimensions.height,
      weight: box.dimensions.weight,
      is_custom_box: false,
      box_id: box.box_id ?? '',
    };
    if (box.box_id) {
      (newBox as any).box_id_manual = true;
    }
    boxes.value.push(newBox);
  });
  if (typeof next_box_id === 'number') {
    nextBoxIdCounter.value = next_box_id;
  }
  isEditting.value++;
};

// Public initializer to be called on open or after edit
const initDeliveryNotes = async (delivery_note_id: number | null = null) => {
  deliveryNoteId.value = delivery_note_id;
  loading.value = true;

  // Fetch delivery note data FIRST to get next_box_id from server
  await fetchDeliveryNoteData(delivery_note_id);

  // Fetch box options if not already loaded
  if (!boxOptions.value || boxOptions.value.length === 0) {
    await fetchDeliveryBoxes();
  } else if (!delivery_note_id && boxes.value.length === 0 && boxOptions.value?.length > 0) {
    // If box options already exist and this is a new delivery note with no boxes yet,
    // add the first box with generateBoxId (extracts box keyword from label) and increment counter
    const firstBox = JSON.parse(JSON.stringify({ ...boxOptions.value[0] }));
    if (nextBoxIdCounter.value > 0) {
      firstBox.box_id = generateBoxId(firstBox.label);
      nextBoxIdCounter.value++; // Increment for next box
    }
    boxes.value.push(firstBox);
  }

  loading.value = false;
};

const saveDeliveryNotes = async () => {
  loading.value = true;

  if (!deliveyNotesTableRef.value) {
    loading.value = false;
    return;
  }

  const allRows = deliveyNotesTableRef.value.exportRows();
  const rows = allRows;

  // --- Validation section ---
  if (rows.length === 0) {
    loading.value = false;
    showToast('There must be at least one row', 'info');
    return;
  }

  const validBoxes = boxes.value.every(
    (box) =>
      parseFloat(box.length) > 0 &&
      parseFloat(box.width) > 0 &&
      parseFloat(box.height) > 0 &&
      parseFloat(box.weight) > 0
  );
  if (!validBoxes) {
    loading.value = false;
    showToast('Please provide valid dimensions and weight for each box', 'info');
    return;
  }

  // Check each row has a box assigned
  const missingBoxes = rows
    .filter((row) => !row.not_included)
    .some((row) => checkIsEmpty(row.box) || !row.box.id);
  if (missingBoxes) {
    loading.value = false;
    showToast('Please assign a box to each item', 'info');
    return;
  }

  // Check every box has a non-empty label
  const invalidLabels = boxes.value.some((box) => !box.label || !box.label.trim());
  if (invalidLabels) {
    loading.value = false;
    showToast('Please provide a label for each box', 'info');
    return;
  }

  let shipping_address = null as Record<string, any> | null;
  let isAddressChanged = false;
  if (rawAddress.value.edited) {
    isAddressChanged = compareAddressObjects(edittedAddress.value, rawAddress.value.edited);
  } else {
    isAddressChanged =
      edittedAddress.value &&
      compareAddressObjects(edittedAddress.value, rawAddress.value.original);
  }
  shipping_address = isAddressChanged ? { ...edittedAddress.value } : null;

  // --- Build payload with COMPLETE box information in lines ---
  const payload = {
    order_item_id: props.order_info.order_item_id,
    delivery_note_id: deliveryNoteId.value,
    address: isAddressChanged ? shipping_address : undefined,
    boxes: boxes.value,
    lines: rows.map((row) => ({
      line_id: row.line_id,
      size: row.size,
      has_name_number: row.has_name_number,
      factory_product_id: row.factory_product_id,
      name: row.name,
      number: row.number,
      sku_number: row.sku_number,
      product_name: row.product_name,
      delivery_note_id: row.delivery_note_id,
      qty_ordered: row.qty_ordered,
      qty_to_ship: row.not_included ? 0 : row.qty_to_ship,
      qty_outstanding: row.qty_outstanding,
      already_shipped: row.already_shipped,
      qty_original: row.qty_original,
      box_id: row.box ? {
        label: row.box.label,
        box_id: row.box.box_id,
        id: row.box.id,
      } : null,
      not_included: row.not_included,
    })),
  };

  try {
    if (deliveryNoteId.value) {
      await http.put(`/delivery-note/${deliveryNoteId.value}`, payload);
    } else {
      await http.post('/delivery-note', payload);
    }

    emits('save');
  } catch (error: any) {
    // Handle box ID conflict errors
    if (error?.response?.data?.errors?.box_ids) {
      showToast(error.response.data.errors.box_ids[0], 'error');
      // Refresh data to get new box IDs
      await initDeliveryNotes(deliveryNoteId.value);
    } else {
      handleResponseException(error);
    }
  } finally {
    loading.value = false;
  }
};
// Input helper functions
// - Integer fields (length/width/height): disallow '.' and non-digits. Paste is sanitized to digits only.
// - Weight field: allows a single '.' and at most one digit after the decimal. Paste is sanitized accordingly.
function isNavigationKey(key: string) {
  return [
    'Backspace',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Delete',
    'Home',
    'End',
  ].includes(key);
}

function onKeydownInteger(e: KeyboardEvent) {
  const key = e.key;
  if (isNavigationKey(key)) return;
  // disallow dot, e, +, - and any non-digit
  if (key === '.' || key === 'e' || key === 'E' || key === '+' || key === '-') {
    e.preventDefault();
    return;
  }

  // allow only digits
  if (!/^[0-9]$/.test(key)) {
    e.preventDefault();
  }
}

function onPasteInteger(box: any, field: keyof typeof box, e: ClipboardEvent) {
  if (!e.clipboardData) return;
  e.preventDefault();
  const paste = e.clipboardData.getData('text') || '';
  const digits = paste.replace(/\D/g, '');
  if (digits === '') return;
  // insert digits at caret position
  const input = e.target as HTMLInputElement;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  const newVal = input.value.slice(0, start) + digits + input.value.slice(end);
  box[field] = newVal.replace(/^0+(?=\d)/, '');
}

function onKeydownWeight(e: KeyboardEvent) {
  const key = e.key;
  if (isNavigationKey(key)) return;
  const input = e.target as HTMLInputElement;
  const value = input.value || '';
  const selStart = input.selectionStart ?? value.length;
  const selEnd = input.selectionEnd ?? selStart;

  if (key === 'e' || key === 'E' || key === '+' || key === '-') {
    e.preventDefault();
    return;
  }

  if (key === '.') {
    // only allow one dot
    if (value.includes('.') && !(selStart <= value.indexOf('.') && selEnd > value.indexOf('.'))) {
      e.preventDefault();
    }
    return;
  }

  if (/^[0-9]$/.test(key)) {
    const dotIndex = value.indexOf('.');
    if (dotIndex !== -1) {
      // if caret is after dot (and not replacing a selection that includes the dot), ensure max 1 fractional digit
      if (selStart > dotIndex && !(selStart < selEnd && selEnd <= value.length && selStart <= dotIndex)) {
        const frac = value.slice(dotIndex + 1);
        // if selection replaces some fractional digits, allow; otherwise block when already 1 digit
        if (!(selStart < selEnd && selEnd > dotIndex) && frac.length >= 1) {
          e.preventDefault();
        }
      }
    }
    return;
  }

  // other keys -> prevent
  e.preventDefault();
}

function onPasteWeight(box: any, field: keyof typeof box, e: ClipboardEvent) {
  if (!e.clipboardData) return;
  e.preventDefault();
  const paste = e.clipboardData.getData('text') || '';
  // keep only digits and dots
  let raw = String(paste ?? '');

  // remove unsupported characters and keep digits/dot
  raw = raw.replace(/[eE+\-]/g, '').replace(/[^0-9.]/g, '');

  // keep only first dot and at most 1 fractional digit
  const firstDot = raw.indexOf('.');
  if (firstDot !== -1) {
    const intPart = raw.slice(0, firstDot);
    const fracPart = raw.slice(firstDot + 1).replace(/\./g, '');
    raw = intPart + '.' + fracPart.slice(0, 1); // enforce max 1 decimal digit
  } else {
    // ensure only digits if no decimal point
    raw = raw.replace(/\./g, '');
  }

  const val = parseFloat(raw);

  if (isNaN(val) || val < 0) {
    return;
  }

  if (val > 1000) {
    box[field] = 1000;
    return;
  }

  // store with exactly one decimal (never exceeds 1 decimal place)
  box[field] = Number(val.toFixed(1));
}

function sanitizeInteger(box: any, field: keyof typeof box) {
  let raw = String(box[field] ?? '');
  // strip everything except digits
  raw = raw.replace(/\D/g, '');
  if (raw === '') {
    box[field] = '';
    return;
  }
  // remove leading zeros
  raw = raw.replace(/^0+(?=\d)/, '');
  box[field] = String(parseInt(raw, 10));
}

function sanitizeWeightInteger(box: any, field: keyof typeof box) {
  let raw = String(box[field] ?? '');

  // remove unsupported characters and keep digits/dot
  raw = raw.replace(/[eE+\-]/g, '').replace(/[^0-9.]/g, '');

  // keep only first dot and at most 1 fractional digit
  const firstDot = raw.indexOf('.');
  if (firstDot !== -1) {
    const intPart = raw.slice(0, firstDot);
    const fracPart = raw.slice(firstDot + 1).replace(/\./g, '');
    raw = intPart + '.' + fracPart.slice(0, 1); // enforce max 1 decimal digit
  } else {
    // ensure only digits if no decimal point
    raw = raw.replace(/\./g, '');
  }

  const val = parseFloat(raw);

  if (isNaN(val) || val < 0) {
    return;
  }

  if (val > 1000) {
    box[field] = 1000;
    return;
  }

  // store with exactly one decimal (never exceeds 1 decimal place)
  box[field] = Number(val.toFixed(1));
}

function compareAddressObjects(obj1, obj2) {
  const keys = ['name', 'address1', 'address2', 'postalCode', 'city', 'state', 'country'];

  for (let key of keys) {
    if (obj1[key] !== obj2[key]) {
      return true; // mismatch found
    }
  }
  return false; // all fields match
}

defineExpose({ initDeliveryNotes, onKeydownWeight, onPasteWeight, onKeydownInteger, onPasteInteger, sanitizeWeightInteger, sanitizeInteger });
</script>
<style lang="scss" scoped>
.btn {
  @apply px-2 py-2 flex gap-2 justify-between items-center rounded-lg capitalize text-ink-soft bg-surface-2 text-nowrap;
}
.input {
  @apply mt-1 block w-full rounded-[4px] outline-none px-2 py-1 border-[1px] border-[#3c3c3c42] focus:border-transparent;
  &.has-error {
    @apply border-[#ff0000] focus:ring-0;
  }
}
:disabled {
  background-color: rgb(237 237 237 / var(--tw-bg-opacity, 1)) !important;
}
</style>
